import clsx from 'clsx'
import ModalBase from 'LuxKit/Modal/ModalBase'
import { DrawerSlideDownAnimation, DrawerSlideUpAnimation } from 'LuxKit/Modal/ModalSheet'
import { MODAL_TRANSITION_DURATION } from 'LuxKit/Modal/modalConstants'
import { rem } from 'polished'
import type { ReactNode } from 'react'
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled, { keyframes } from 'styled-components'
import zIndex from 'styles/tools/z-index'
import useHasMounted from 'hooks/useHasMounted'
import { mediaQueryDown, mediaQueryUp } from 'components/utils/breakpoint'
import TripDrawerHeader from './TripDrawerHeader'
import { useTripDrawerPresentation, type TripDrawerPresentation } from './TripDrawerPresentationContext'

export type TripDrawerAnimation = 'enter-and-exit' | 'enter-only' | 'none'

export const DRAWER_FOOTER_SCROLL_FALLBACK_PX = 96
export const DRAWER_FOOTER_SCROLL_EXTRA_PX = 12

const slideInFromLeft = keyframes`
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
`

const slideOutToLeft = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-100%);
  }
`

const slideInFromRight = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`

const slideOutToRight = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
`

const backdropAppear = keyframes`
  from {
    background-color: rgba(0, 0, 0, 0);
    backdrop-filter: blur(0px);
  }
  to {
    background-color: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(2px);
  }
`

const backdropDisappear = keyframes`
  from {
    background-color: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(2px);
  }
  to {
    background-color: rgba(0, 0, 0, 0);
    backdrop-filter: blur(0px);
  }
`

const TRIP_DRAWER_MAX_WIDTH_PX = 960

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  animation-duration: ${MODAL_TRANSITION_DURATION}ms;
  animation-fill-mode: forwards;
  animation-timing-function: ease-out;
  animation-name: ${backdropAppear};

  &.closing {
    animation-name: ${backdropDisappear};
  }
`

const Container = styled.div<{ $maxWidth: number }>`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 100%;
  max-width: none;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  z-index: 2;
  animation-duration: ${MODAL_TRANSITION_DURATION}ms;
  animation-fill-mode: forwards;
  animation-timing-function: ease-out;

  &.direction-left-to-right {
    inset-inline-start: 0;
    animation-name: ${slideInFromLeft};
    box-shadow: ${rem(4)} 0 ${rem(24)} rgba(15, 23, 42, 0.15);

    &.closing {
      animation-name: ${slideOutToLeft};
    }

    [dir="rtl"] & {
      animation-name: ${slideInFromRight};
      box-shadow: ${rem(-4)} 0 ${rem(24)} rgba(15, 23, 42, 0.15);
    }

    [dir="rtl"] &.closing {
      animation-name: ${slideOutToRight};
    }
  }

  &.direction-right-to-left {
    inset-inline-end: 0;
    animation-name: ${slideInFromRight};
    box-shadow: ${rem(-4)} 0 ${rem(24)} rgba(15, 23, 42, 0.15);

    &.closing {
      animation-name: ${slideOutToRight};
    }

    [dir="rtl"] & {
      animation-name: ${slideInFromLeft};
      box-shadow: ${rem(4)} 0 ${rem(24)} rgba(15, 23, 42, 0.15);
    }

    [dir="rtl"] &.closing {
      animation-name: ${slideOutToLeft};
    }
  }

  &.with-overlay {
    box-shadow: none;
  }

  ${mediaQueryUp.tablet} {
    width: 75%;
    max-width: ${({ $maxWidth }) => rem($maxWidth)};
  }

  &.presentation-right-panel {
    width: 100%;
    max-width: none;
  }

  &.animation-none {
    animation: none;
  }
`

const TripDrawerModal = styled(ModalBase)<{ $maxWidth: number }>`
  &&&.mode-drawer,
  &&&.mode-drawer-starboard {
    width: 100%;
    max-width: none;
    height: 100%;
    overflow: hidden;
  }

  ${mediaQueryDown.mobile} {
    &&&.mode-drawer-starboard.transition-in {
      animation-name: ${DrawerSlideUpAnimation};
    }

    &&&.mode-drawer-starboard.transition-out {
      animation-name: ${DrawerSlideDownAnimation};
    }
  }

  ${mediaQueryUp.tablet} {
    &&&.mode-drawer,
    &&&.mode-drawer-starboard {
      width: 75%;
      max-width: ${({ $maxWidth }) => rem($maxWidth)};
    }
  }

  &&&.animation-none {
    animation: none;
  }
`

const ModalContentFrame = styled.div`
  grid-column: first / last;
  grid-row: first / last;
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  height: 100%;
  box-sizing: border-box;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
`

const Body = styled.div`
  flex: 1;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  min-width: 0;
`

const FooterScrollClearance = styled.div`
  flex-shrink: 0;
  width: 100%;
  pointer-events: none;
`

const FloatingFooter = styled.div`
  position: absolute;
  bottom: 0;
  inset-inline: 0;
  z-index: 10;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
`

const Root = styled.div`
  position: fixed;
  inset: 0;
  z-index: ${zIndex.modal};
  pointer-events: none;
  overflow: hidden;

  & > * {
    pointer-events: auto;
  }

  &.presentation-right-panel {
    position: absolute;
    // Must sit above the cart panel (CartPanelContainer z-index: 7) so drawers
    // opened from cart tiles render on top of the cart instead of behind it.
    z-index: 8;
  }
`

interface Props extends Utils.PropsWithDataAttributes {
  isOpen: boolean
  onClose: () => void
  onAfterClose?: () => void
  direction: 'left-to-right' | 'right-to-left'
  presentation?: TripDrawerPresentation
  showOverlay?: boolean
  children: ReactNode
  header?: ReactNode
  footer?: ReactNode
  onBack?: () => void
  headerActions?: ReactNode
  onDelete?: () => void
  maxWidth?: number
  animation?: TripDrawerAnimation
  contentScrollResetKey?: string | number
}

function TripDrawer(props: Props) {
  const {
    isOpen,
    onClose,
    onAfterClose,
    direction,
    presentation = 'modal',
    showOverlay = true,
    children,
    header,
    footer,
    onBack,
    headerActions,
    onDelete,
    maxWidth = TRIP_DRAWER_MAX_WIDTH_PX,
    animation,
    contentScrollResetKey,
    ...dataAttributes
  } = props
  const { portalElement, registerDrawer, isTopDrawer } = useTripDrawerPresentation()
  const hasMounted = useHasMounted()
  const [shouldRender, setShouldRender] = useState(isOpen)
  const [isClosing, setIsClosing] = useState(false)
  const drawerIdRef = useRef(Symbol('TripDrawer'))
  const bodyRef = useRef<HTMLDivElement>(null)
  const [footerElement, setFooterElement] = useState<HTMLDivElement | null>(null)
  const [footerHeightPx, setFooterHeightPx] = useState(0)
  const hasRightPanelTarget = presentation === 'right-panel' && !!portalElement
  const effectivePresentation: TripDrawerPresentation = hasRightPanelTarget ? 'right-panel' : 'modal'
  const effectiveAnimation = animation ?? (effectivePresentation === 'right-panel' ? 'enter-only' : 'enter-and-exit')
  const shouldUseRightPanelLifecycle = effectivePresentation === 'right-panel'
  const hasFooter = Boolean(footer)
  // Fallback while measure is 0 (common during modal enter).
  const footerScrollClearancePx = hasFooter
    ? (footerHeightPx > 0 ? footerHeightPx : DRAWER_FOOTER_SCROLL_FALLBACK_PX) + DRAWER_FOOTER_SCROLL_EXTRA_PX
    : 0

  const footerRef = useCallback((node: HTMLDivElement | null) => {
    setFooterElement(node)
  }, [])

  useLayoutEffect(() => {
    if (!hasFooter) {
      setFooterHeightPx(0)
      return
    }

    if (!footerElement) return

    const measure = () => {
      setFooterHeightPx(footerElement.getBoundingClientRect().height)
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(footerElement)

    // Modal slide-up can report 0 height on the first frame; remeasure after enter.
    const remeasureTimer = window.setTimeout(measure, MODAL_TRANSITION_DURATION)

    return () => {
      observer.disconnect()
      window.clearTimeout(remeasureTimer)
    }
  }, [footerElement, hasFooter, isOpen])

  useEffect(() => {
    if (!shouldUseRightPanelLifecycle) {
      return
    }
    if (isOpen) {
      setShouldRender(true)
      setIsClosing(false)
    } else if (shouldRender) {
      if (effectiveAnimation !== 'enter-and-exit') {
        setShouldRender(false)
        setIsClosing(false)
        onAfterClose?.()
        return
      }

      setIsClosing(true)
      const timer = setTimeout(() => {
        setShouldRender(false)
        setIsClosing(false)
        onAfterClose?.()
      }, MODAL_TRANSITION_DURATION)
      return () => clearTimeout(timer)
    }
  }, [effectiveAnimation, isOpen, onAfterClose, shouldRender, shouldUseRightPanelLifecycle])

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.defaultPrevented) return
    if (!isTopDrawer(drawerIdRef.current)) return
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose()
    }
  }, [isTopDrawer, onClose])

  useEffect(() => {
    if (shouldUseRightPanelLifecycle && shouldRender) {
      const unregisterDrawer = registerDrawer(drawerIdRef.current)
      document.addEventListener('keydown', handleKeyDown)
      return () => {
        document.removeEventListener('keydown', handleKeyDown)
        unregisterDrawer()
      }
    }
  }, [handleKeyDown, registerDrawer, shouldRender, shouldUseRightPanelLifecycle])

  useEffect(() => {
    if (contentScrollResetKey == null) return
    bodyRef.current?.scrollTo({ top: 0 })
  }, [contentScrollResetKey])

  const handleOverlayClick = useCallback(() => {
    onClose()
  }, [onClose])

  if (!hasMounted || typeof document === 'undefined') {
    return null
  }

  const portalTarget = hasRightPanelTarget ? portalElement : document.body

  const isRtl = document.documentElement.dir === 'rtl'
  const closeOnLeft = direction === 'right-to-left' ? !isRtl : isRtl
  const shouldShowOverlay = effectivePresentation === 'modal' && showOverlay
  const drawerContent = (
    <>
      <TripDrawerHeader
        closeOnLeft={closeOnLeft}
        onClose={onClose}
        onBack={onBack}
        onDelete={onDelete}
        headerActions={headerActions}
      >
        {header}
      </TripDrawerHeader>
      <Body ref={bodyRef}>
        {children}
        {hasFooter && (
          <FooterScrollClearance
            aria-hidden="true"
            data-testid="trip-drawer-footer-scroll-clearance"
            style={{ height: `${footerScrollClearancePx}px` }}
          />
        )}
      </Body>
      {hasFooter && <FloatingFooter ref={footerRef}>{footer}</FloatingFooter>}
    </>
  )

  if (effectivePresentation === 'modal') {
    return (
      <TripDrawerModal
        $maxWidth={maxWidth}
        className={clsx({ 'animation-none': effectiveAnimation === 'none' })}
        isOpen={isOpen}
        mode={direction === 'right-to-left' ? 'drawer-starboard' : 'drawer'}
        dismissible
        onAfterClose={onAfterClose}
        onClose={onClose}
      >
        <ModalContentFrame {...dataAttributes}>
          {drawerContent}
        </ModalContentFrame>
      </TripDrawerModal>
    )
  }

  if (!shouldRender) {
    return null
  }

  return createPortal(
    <Root {...dataAttributes} className={`presentation-${effectivePresentation}`}>
      {shouldShowOverlay && (
        <Overlay
          className={clsx({ closing: isClosing })}
          onClick={handleOverlayClick}
        />
      )}
      <Container
        $maxWidth={maxWidth}
        className={clsx(
          `direction-${direction}`,
          `presentation-${effectivePresentation}`,
          `animation-${effectiveAnimation === 'none' ? 'none' : 'enabled'}`,
          { closing: isClosing },
          { 'with-overlay': shouldShowOverlay },
        )}
      >
        {drawerContent}
      </Container>
    </Root>,
    portalTarget,
  )
}

export default TripDrawer
